import { useCallback, useMemo } from 'react';
import { ActiveSelection, FabricObject, Point } from 'fabric';
import { useDesignStore, useProductSelectionStore } from '@/stores';
import { debounce } from '@/lib/utils';
import { getPrintAreaCenter } from '@/utils/printAreaBounds';

/**
 * Hook for object operations on the canvas
 */
export function useObjectOperations() {
  const { canvas, selectedObjects, saveToHistory } = useDesignStore();
  const printContext = useProductSelectionStore((s) => s.activePrintContext);

  /**
   * Delete selected objects
   */
  const deleteSelected = useCallback(() => {
    if (!canvas || selectedObjects.length === 0) return;

    selectedObjects.forEach((obj) => {
      canvas.remove(obj);
    });
    canvas.discardActiveObject();
    canvas.requestRenderAll();
    saveToHistory('delete');
  }, [canvas, selectedObjects, saveToHistory]);

  /**
   * Duplicate selected objects
   */
  const duplicateSelected = useCallback(async () => {
    if (!canvas || selectedObjects.length === 0) return;

    const clonedObjects: FabricObject[] = [];

    for (const obj of selectedObjects) {
      const cloned = await obj.clone();
      // A cloned embroidery motif would carry the same source key and mapping,
      // giving the position two motifs — the modal would then edit whichever one
      // it found first, and only one of them could ever reach the database.
      // Stripping both makes the clone an ordinary image; the publish rule
      // rejects those on an embroidery position, which is the honest outcome.
      if (cloned.embFileKey) {
        cloned.embFileKey = undefined;
        cloned.confirmedMapping = undefined;
      }
      cloned.set({
        left: (cloned.left || 0) + 20,
        top: (cloned.top || 0) + 20,
      });
      canvas.add(cloned);
      clonedObjects.push(cloned);
    }

    // Select the cloned objects
    const singleClone = clonedObjects[0];
    if (clonedObjects.length === 1 && singleClone) {
      canvas.setActiveObject(singleClone);
    } else if (clonedObjects.length > 1) {
      const selection = new ActiveSelection(clonedObjects, { canvas });
      canvas.setActiveObject(selection);
    }

    canvas.requestRenderAll();
    saveToHistory('duplicate');
  }, [canvas, selectedObjects, saveToHistory]);

  /**
   * Bring selected objects to front
   */
  const bringToFront = useCallback(() => {
    if (!canvas || selectedObjects.length === 0) return;

    selectedObjects.forEach((obj) => {
      canvas.bringObjectToFront(obj);
    });
    canvas.requestRenderAll();
    saveToHistory('bringToFront');
  }, [canvas, selectedObjects, saveToHistory]);

  /**
   * Send selected objects to back
   */
  const sendToBack = useCallback(() => {
    if (!canvas || selectedObjects.length === 0) return;

    selectedObjects.forEach((obj) => {
      canvas.sendObjectToBack(obj);
    });
    canvas.requestRenderAll();
    saveToHistory('sendToBack');
  }, [canvas, selectedObjects, saveToHistory]);

  /**
   * Bring selected objects forward (one level)
   */
  const bringForward = useCallback(() => {
    if (!canvas || selectedObjects.length === 0) return;

    selectedObjects.forEach((obj) => {
      canvas.bringObjectForward(obj);
    });
    canvas.requestRenderAll();
    saveToHistory('bringForward');
  }, [canvas, selectedObjects, saveToHistory]);

  /**
   * Send selected objects backward (one level)
   */
  const sendBackward = useCallback(() => {
    if (!canvas || selectedObjects.length === 0) return;

    selectedObjects.forEach((obj) => {
      canvas.sendObjectBackwards(obj);
    });
    canvas.requestRenderAll();
    saveToHistory('sendBackward');
  }, [canvas, selectedObjects, saveToHistory]);

  /**
   * Flip selected objects horizontally
   */
  const flipHorizontal = useCallback(() => {
    if (!canvas || selectedObjects.length === 0) return;

    selectedObjects.forEach((obj) => {
      obj.set('flipX', !obj.flipX);
    });
    canvas.requestRenderAll();
    saveToHistory('flipHorizontal');
  }, [canvas, selectedObjects, saveToHistory]);

  /**
   * Flip selected objects vertically
   */
  const flipVertical = useCallback(() => {
    if (!canvas || selectedObjects.length === 0) return;

    selectedObjects.forEach((obj) => {
      obj.set('flipY', !obj.flipY);
    });
    canvas.requestRenderAll();
    saveToHistory('flipVertical');
  }, [canvas, selectedObjects, saveToHistory]);

  /**
   * Nudge selected objects by a given amount
   */
  const nudge = useCallback(
    (dx: number, dy: number) => {
      if (!canvas || selectedObjects.length === 0) return;

      selectedObjects.forEach((obj) => {
        obj.set({
          left: (obj.left || 0) + dx,
          top: (obj.top || 0) + dy,
        });
        obj.setCoords();
      });

      canvas.requestRenderAll();
    },
    [canvas, selectedObjects]
  );

  /**
   * Save history after nudging (debounced)
   */
  const saveNudgeHistory = useMemo(() => {
    return debounce(() => {
      saveToHistory('nudge');
    }, 500);
  }, [saveToHistory]);

  /**
   * Nudge with history save
   */
  const nudgeWithHistory = useCallback(
    (dx: number, dy: number) => {
      nudge(dx, dy);
      saveNudgeHistory();
    },
    [nudge, saveNudgeHistory]
  );

  /**
   * Select all objects
   */
  const selectAll = useCallback(() => {
    if (!canvas) return;

    canvas.discardActiveObject();
    const objects = canvas
      .getObjects()
      .filter((obj) => obj.selectable !== false);

    if (objects.length === 0) return;

    const single = objects[0];
    if (objects.length === 1 && single) {
      canvas.setActiveObject(single);
    } else {
      const selection = new ActiveSelection(objects, { canvas });
      canvas.setActiveObject(selection);
    }

    canvas.requestRenderAll();
  }, [canvas]);

  /**
   * Deselect all objects
   */
  const deselectAll = useCallback(() => {
    if (!canvas) return;

    canvas.discardActiveObject();
    canvas.requestRenderAll();
  }, [canvas]);

  /**
   * Center selected objects horizontally within the print area
   */
  const centerHorizontal = useCallback(() => {
    if (!canvas || selectedObjects.length === 0) return;

    const center = getPrintAreaCenter(canvas, printContext);
    if (!center) return;
    const targetCenterX = center.x;

    selectedObjects.forEach((obj) => {
      const currentCenter = obj.getCenterPoint();
      obj.setPositionByOrigin(
        new Point(targetCenterX, currentCenter.y),
        'center',
        'center'
      );
      obj.setCoords();
    });

    canvas.requestRenderAll();
    saveToHistory('centerHorizontal');
  }, [canvas, selectedObjects, saveToHistory, printContext]);

  /**
   * Center selected objects vertically within the print area
   */
  const centerVertical = useCallback(() => {
    if (!canvas || selectedObjects.length === 0) return;

    const center = getPrintAreaCenter(canvas, printContext);
    if (!center) return;
    const targetCenterY = center.y;

    selectedObjects.forEach((obj) => {
      const currentCenter = obj.getCenterPoint();
      obj.setPositionByOrigin(
        new Point(currentCenter.x, targetCenterY),
        'center',
        'center'
      );
      obj.setCoords();
    });

    canvas.requestRenderAll();
    saveToHistory('centerVertical');
  }, [canvas, selectedObjects, saveToHistory, printContext]);

  /**
   * Set object opacity
   */
  const setOpacity = useCallback(
    (opacity: number) => {
      if (!canvas || selectedObjects.length === 0) return;

      selectedObjects.forEach((obj) => {
        obj.set('opacity', Math.max(0, Math.min(1, opacity)));
      });

      canvas.requestRenderAll();
      saveToHistory('setOpacity');
    },
    [canvas, selectedObjects, saveToHistory]
  );

  /**
   * Lock/unlock selected objects
   */
  const toggleLock = useCallback(() => {
    if (!canvas || selectedObjects.length === 0) return;

    selectedObjects.forEach((obj) => {
      const isLocked = obj.lockMovementX;
      obj.set({
        lockMovementX: !isLocked,
        lockMovementY: !isLocked,
        lockRotation: !isLocked,
        lockScalingX: !isLocked,
        lockScalingY: !isLocked,
        hasControls: isLocked, // Show controls when unlocked
      });
    });

    canvas.requestRenderAll();
    saveToHistory('toggleLock');
  }, [canvas, selectedObjects, saveToHistory]);

  return {
    deleteSelected,
    duplicateSelected,
    bringToFront,
    sendToBack,
    bringForward,
    sendBackward,
    flipHorizontal,
    flipVertical,
    nudge,
    nudgeWithHistory,
    selectAll,
    deselectAll,
    centerHorizontal,
    centerVertical,
    setOpacity,
    toggleLock,
    hasSelection: selectedObjects.length > 0,
  };
}
